import { useEffect, useRef, useState } from 'react'

import { useChatChannelConfig } from '@liveHelper/context/ChatChannelConfigContext'
import { actionsSelector, useStore } from '@liveHelper/models/store'
import {
  useTrackCalendlyDateAndTimeSelected,
  useTrackCalendlyEventScheduled,
  useTrackCalendlyEventTypeViewed,
} from '@liveHelper/tracking/useLiveHelperTracking'
import { Close } from '@oneToOne/shared/assets/Close'
import { AnimatedLoadingSVG } from '@oneToOne/shared/components/shared.styles'

import { useContextTranslation } from '@core/i18n'
import { logError } from '@core/logging'

import {
  CalendlyContainer,
  CenterContentContainer,
  CloseButton,
  LoadingText,
  ScheduledInfoText,
  SchedulingContainer,
  SuccessCheckmarkSVG,
} from './Scheduling.styles'

/**
 * Scheduling component with Calendly integration
 */
export const Scheduling = () => {
  const chatChannelConfig = useChatChannelConfig()
  const actions = useStore(actionsSelector)
  const { t } = useContextTranslation()
  const calendlyRef = useRef<HTMLDivElement>(null)

  const [scheduled, setScheduled] = useState(false)

  const trackCalendlyEventTypeViewed = useTrackCalendlyEventTypeViewed()
  const trackCalendlyDateAndTimeSelected = useTrackCalendlyDateAndTimeSelected()
  const trackCalendlyEventTypeScheduled = useTrackCalendlyEventScheduled()

  const initializeCalendly = async () => {
    if (
      chatChannelConfig?.config?.scheduling_provider !==
        'SCHEDULING_PROVIDER_CALENDLY' ||
      !chatChannelConfig?.config?.calendly_account?.scheduling_url
    ) {
      logError(new Error('No scheduling url found for Calendly'), {
        captureContext: {
          extra: {
            chatChannelConfigId: chatChannelConfig!.id,
          },
        },
      })
      actions.sendUIStateTransitionEvent('MINIMIZE')
      return
    }

    // Calendly have no ready event, so we need to poll for it
    await new Promise((resolve) => {
      const intervalId = setInterval(() => {
        if (
          // @ts-expect-error Expected
          window.Calendly &&
          // @ts-expect-error Expected
          typeof window.Calendly.initInlineWidget === 'function'
        ) {
          resolve(true)
          clearInterval(intervalId)
        }
      }, 50)
    })

    // @ts-expect-error Expected
    window.Calendly.initInlineWidget({
      url: chatChannelConfig.config.calendly_account.scheduling_url,
      parentElement: calendlyRef.current,
      prefill: {},
      utm: {},
    })
  }

  useEffect(() => {
    const callback = (e) => {
      if (e.data.event === 'calendly.event_type_viewed') {
        trackCalendlyEventTypeViewed()
      }
      if (e.data.event === 'calendly.date_and_time_selected') {
        trackCalendlyDateAndTimeSelected()
      }
      if (e.data.event === 'calendly.event_scheduled') {
        setScheduled(true)
        trackCalendlyEventTypeScheduled({ calendly: e.data.payload })
      }
    }
    window.addEventListener('message', callback)
    return () => {
      window.removeEventListener('message', callback)
    }
  }, [
    trackCalendlyEventTypeViewed,
    trackCalendlyDateAndTimeSelected,
    trackCalendlyEventTypeScheduled,
  ])

  useEffect(() => {
    let timeout
    if (scheduled) {
      timeout = setTimeout(() => {
        actions.sendUIStateTransitionEvent('MINIMIZE')
      }, 5000)
    }
    return () => {
      clearTimeout(timeout)
    }
  }, [
    scheduled,
    actions,
    trackCalendlyEventTypeScheduled,
    trackCalendlyEventTypeViewed,
    trackCalendlyDateAndTimeSelected,
  ])

  return (
    <SchedulingContainer>
      <script
        onLoad={initializeCalendly}
        type="text/javascript"
        src="https://assets.calendly.com/assets/external/widget.js"
      />
      {scheduled ? (
        <CenterContentContainer>
          <ScheduledInfoText>{t('oneToOne:Thank you')}</ScheduledInfoText>
          <SuccessCheckmarkSVG label="success" />
        </CenterContentContainer>
      ) : (
        <>
          <CenterContentContainer>
            <LoadingText>{t('oneToOne:One moment please...')}</LoadingText>
            <AnimatedLoadingSVG label="loading" />
          </CenterContentContainer>
          <CalendlyContainer ref={calendlyRef} />
        </>
      )}
      <CloseButton
        shape="circle"
        aria-label={t(`oneToOne:End Scheduling`)}
        background="var(--white)"
        buttonSize="sm"
        onClick={(e) => {
          e.stopPropagation()
          actions.sendUIStateTransitionEvent('MINIMIZE')
        }}
        icon={<Close width="16px" fill="var(--gray-9)" label="close" />}
      />
    </SchedulingContainer>
  )
}
